import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import { ShieldAlert, Loader2, Filter } from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const severityColors = {
  info: 'bg-blue-500/20 text-blue-500',
  warning: 'bg-yellow-500/20 text-yellow-500',
  critical: 'bg-red-500/20 text-red-500',
};

const typeColors = {
  login_success: 'bg-green-500/20 text-green-500',
  login_failed: 'bg-red-500/20 text-red-500',
  logout: 'bg-gray-500/20 text-gray-400',
  unauthorized_access: 'bg-orange-500/20 text-orange-500',
  account_suspended: 'bg-purple-500/20 text-purple-500',
  user_deleted: 'bg-red-500/20 text-red-500',
};

const typeOptions = ['all', 'login_success', 'login_failed', 'logout', 'unauthorized_access', 'account_suspended', 'user_deleted'];

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const SecurityEvents = () => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState('all');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const qs = eventType !== 'all' ? `event_type=${eventType}&limit=100` : 'limit=100';
        const data = await auditApi.getSecurity(qs);
        if (active) { setEvents(data.items); setTotal(data.total); }
      } catch (err) {
        toast.error(err.message || 'Failed to load security events');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [eventType]);

  return (
    <MainLayout title="Security Events" subtitle="Authentication & access security log">
      <div data-testid="security-events-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-gray-500">
            <ShieldAlert className="w-5 h-5" />
            <span className="text-sm">{total} security events</span>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="px-4 py-2 rounded-xl glass-card bg-transparent outline-none capitalize"
              data-testid="security-filter-type"
            >
              {typeOptions.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20" data-testid="security-loading">
              <Loader2 className="w-7 h-7 animate-spin text-blue-500" />
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                  <th className="px-6 py-4 text-left">Time</th>
                  <th className="px-6 py-4 text-left">Event</th>
                  <th className="px-6 py-4 text-left">Severity</th>
                  <th className="px-6 py-4 text-left">Actor</th>
                  <th className="px-6 py-4 text-left">IP</th>
                </tr>
              </thead>
              <tbody data-testid="security-table-body">
                {events.length === 0 ? (
                  <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-500">No security events.</td></tr>
                ) : events.map((ev, i) => (
                  <motion.tr
                    key={ev.id}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                    className="border-b border-gray-500/10 hover:bg-blue-500/5"
                  >
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{fmt(ev.timestamp)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${typeColors[ev.event_type] || 'bg-gray-500/20 text-gray-500'}`}>
                        {(ev.event_type || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs rounded-full capitalize ${severityColors[ev.severity] || 'bg-gray-500/20 text-gray-500'}`}>
                        {ev.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{ev.actor_email || 'anonymous'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 font-mono">{ev.ip_address || '—'}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default SecurityEvents;
